import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddTaskIcon from '@mui/icons-material/AddTask';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useBrand } from '../contexts/BrandContext';
import { useAuth } from '../contexts/AuthContext';
import { auditsApi } from '../services/audits';
import { tasksApi } from '../services/tasks';
import { api } from '../services/api';
import QueryManager from '../components/brand/QueryManager';
import type {
  Audit,
  AuditType,
  AgentStatus,
  BrandNarrativeSummary,
  BrandAuthoritySummary,
  BrandReferralSummary,
  BrandQueryAxis,
  BrandStrategy,
  GeoStrategy,
} from '@traaker/shared';

type AxisAgent = 'brand_narrative' | 'brand_authority' | 'brand_referral';

const AXIS_META: Record<BrandQueryAxis, {
  title: string;
  subtitle: string;
  auditType: AuditType;
  agentType: AxisAgent;
  color: string;
}> = {
  narrative: {
    title: 'Brand Narrative',
    subtitle:
      'Ce que les moteurs IA disent de votre marque quand on les interroge directement. L’alignement mesure si la perception IA correspond à votre Brand Knowledge.',
    auditType: 'brand_narrative',
    agentType: 'brand_narrative',
    color: '#8B5CF6',
  },
  authority: {
    title: 'Brand Authority',
    subtitle:
      'Votre site est-il utilisé comme SOURCE par les moteurs IA pour les questions de votre domaine ? L’autorité thématique mesure le taux de citation de votre domaine.',
    auditType: 'brand_authority',
    agentType: 'brand_authority',
    color: '#00DDB3',
  },
  referral: {
    title: 'Brand Referral',
    subtitle:
      'Les moteurs IA vous recommandent-ils quand un prospect cherche une solution ? L’échelle de visibilité teste 5 niveaux — du plus générique (L1) au plus spécifique (L5).',
    auditType: 'brand_referral',
    agentType: 'brand_referral',
    color: '#FF6B35',
  },
};

const STATUS_COLORS: Record<string, 'default' | 'info' | 'success' | 'error'> = {
  pending: 'default',
  running: 'info',
  completed: 'success',
  failed: 'error',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'En attente',
  running: 'En cours',
  completed: 'Terminé',
  failed: 'Échoué',
};

const AGENT_STATUS_COLORS: Record<AgentStatus, string> = {
  pending: '#A0A4AE',
  running: '#F59E0B',
  completed: '#10B981',
  failed: '#EF4444',
};

const ALIGNMENT_COLORS: Record<string, string> = {
  strong: '#00DDB3',
  partial: '#FF6B35',
  weak: '#FF4444',
  misaligned: '#FF4444',
};

const ALIGNMENT_LABELS: Record<string, string> = {
  strong: 'Fort',
  partial: 'Partiel',
  weak: 'Faible',
  misaligned: 'Désaligné',
};

const LEVEL_LABELS: Record<string, string> = {
  L1: 'L1 — Head term',
  L2: 'L2 — Qualifié',
  L3: 'L3 — Use-case',
  L4: 'L4 — Local',
  L5: 'L5 — Long-tail',
};

const LEVEL_WEIGHTS: Record<string, number> = {
  L1: 0.05,
  L2: 0.10,
  L3: 0.25,
  L4: 0.30,
  L5: 0.30,
};

function ScoreBar({ score, label }: { score: number; label: string }) {
  const color = score >= 70 ? '#00DDB3' : score >= 40 ? '#FF6B35' : '#FF4444';
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="body2" fontWeight={700} sx={{ color }}>{score}/100</Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={score}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: 'rgba(11,11,15,0.04)',
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 4 },
        }}
      />
    </Box>
  );
}

function SummaryCard({ title, value, suffix, color }: {
  title: string;
  value: string;
  suffix: string;
  color: string;
}) {
  return (
    <Card>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography variant="caption" color="text.secondary">{title}</Typography>
        <Typography variant="h5" fontWeight={700} sx={{ color }}>
          {value}{suffix}
        </Typography>
      </CardContent>
    </Card>
  );
}

function escapeRegex(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function extractDomain(url: string) {
  try {
    return url.replace(/^https?:\/\//, '').replace(/\/.*$/, '').toLowerCase();
  } catch {
    return url.toLowerCase();
  }
}

function isBrandUrl(url: string, brandName?: string, brandUrl?: string) {
  if (!brandName && !brandUrl) return false;
  const ref = (brandUrl || brandName || '')
    .replace(/^https?:\/\//, '')
    .replace(/\/.*$/, '')
    .toLowerCase();
  if (!ref) return false;
  return url.toLowerCase().includes(ref);
}

type EngineKey = 'perplexity' | 'openai' | 'gemini' | 'claude' | 'unknown';

const ENGINE_META: Record<EngineKey, { label: string; color: string; bg: string }> = {
  perplexity: { label: 'Perplexity', color: '#20B8CD', bg: '#20B8CD22' },
  openai: { label: 'ChatGPT', color: '#10A37F', bg: '#10A37F22' },
  gemini: { label: 'Gemini', color: '#4285F4', bg: '#4285F422' },
  claude: { label: 'Claude', color: '#D97757', bg: '#D9775722' },
  unknown: { label: 'Moteur', color: '#8B9DC3', bg: '#8B9DC322' },
};

function engineKey(e?: string): EngineKey {
  const k = (e || '').toLowerCase();
  if (k === 'perplexity' || k === 'openai' || k === 'gemini' || k === 'claude') return k;
  return 'unknown';
}

function EngineLogo({ engine, size = 20 }: { engine?: string; size?: number }) {
  const k = engineKey(engine);
  const meta = ENGINE_META[k];
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    style: { display: 'block' as const },
  };
  if (k === 'perplexity') {
    return (
      <svg {...common} fill={meta.color} aria-label="Perplexity">
        <path d="M12 2.5 3.5 7.5v9L12 21.5l8.5-5v-9L12 2.5Zm0 2.31 6.5 3.82v7.74L12 19.19l-6.5-3.82V8.63L12 4.81Z" />
        <path d="M7.5 9.5h9v1.5h-9zM7.5 12h9v1.5h-9z" />
      </svg>
    );
  }
  if (k === 'openai') {
    return (
      <svg {...common} fill={meta.color} aria-label="ChatGPT">
        <path d="M22.28 9.82a5.98 5.98 0 0 0-.52-4.91 6.05 6.05 0 0 0-6.52-2.9A6 6 0 0 0 5.07 4.16a5.98 5.98 0 0 0-4 2.91 6.05 6.05 0 0 0 .75 7.11 5.98 5.98 0 0 0 .52 4.91 6.05 6.05 0 0 0 6.52 2.9A6 6 0 0 0 18.93 19.84a5.98 5.98 0 0 0 4-2.91 6.05 6.05 0 0 0-.65-7.11ZM13.19 21.1a4.49 4.49 0 0 1-2.88-1.04l.14-.08 4.77-2.76a.78.78 0 0 0 .39-.68v-6.75L17.63 11v5.57a4.5 4.5 0 0 1-4.44 4.53Zm-9.66-4.12a4.48 4.48 0 0 1-.54-3.03l.14.08 4.78 2.76a.77.77 0 0 0 .78 0l5.84-3.37v2.33L10.7 18.5a4.5 4.5 0 0 1-6.17-1.52ZM2.56 8.66a4.49 4.49 0 0 1 2.34-1.97v5.68a.77.77 0 0 0 .39.67l5.81 3.35-2.02 1.16a.07.07 0 0 1-.07 0l-4.83-2.79a4.5 4.5 0 0 1-1.62-6.1Zm16.57 3.86-5.83-3.4 2.02-1.16a.07.07 0 0 1 .07 0l4.82 2.79a4.5 4.5 0 0 1-.67 8.1v-5.68a.78.78 0 0 0-.41-.65Zm2.02-3.03-.14-.09L16.23 6.6a.77.77 0 0 0-.79 0L9.62 9.97V7.64l4.83-2.79a4.5 4.5 0 0 1 6.7 4.64ZM8.52 13.57 6.5 12.4V6.84a4.5 4.5 0 0 1 7.38-3.46l-.14.08L8.97 6.22a.78.78 0 0 0-.39.67Zm1.1-2.37L12.22 9.67l2.62 1.5v3.01l-2.6 1.51-2.62-1.5Z" />
      </svg>
    );
  }
  if (k === 'gemini') {
    return (
      <svg {...common} viewBox="0 0 24 24" aria-label="Gemini">
        <defs>
          <linearGradient id="gm" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#4285F4" />
            <stop offset="50%" stopColor="#9B72F2" />
            <stop offset="100%" stopColor="#D96570" />
          </linearGradient>
        </defs>
        <path
          fill="url(#gm)"
          d="M12 2c.4 4.6 2.4 6.6 7 7-4.6.4-6.6 2.4-7 7-.4-4.6-2.4-6.6-7-7 4.6-.4 6.6-2.4 7-7Z"
        />
      </svg>
    );
  }
  if (k === 'claude') {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        fill={meta.color}
        aria-label="Claude"
        style={{ display: 'block' }}
      >
        <path d="m19.6 66.5 19.7-11 .3-1-.3-.5h-1l-3.3-.2-11.2-.3L14 53l-9.5-.5-2.4-.5L0 49l.2-1.5 2-1.3 2.9.2 6.3.5 9.5.6 6.9.4L38 49.1h1.6l.2-.7-.5-.4-.4-.4L29 41l-10.6-7-5.6-4.1-3-2-1.5-2-.6-4.2 2.7-3 3.7.3.9.2 3.7 2.9 8 6.1L37 36l1.5 1.2.6-.4.1-.3-.7-1.1L33 25l-6-10.4-2.7-4.3-.7-2.6c-.3-1-.4-2-.4-3l3-4.2L28 0l4.2.6L33.8 2l2.6 6 4.1 9.3L47 29.9l2 3.8 1 3.4.3 1h.7v-.5l.5-7.2 1-8.7 1-11.2.3-3.2 1.6-3.8 3-2L61 2.6l2 2.9-.3 1.8-1.1 7.7L59 27.1l-1.5 8.2h.9l1-1.1 4.1-5.4 6.9-8.6 3-3.5L77 13l2.3-1.8h4.3l3.1 4.7-1.4 4.9-4.4 5.6-3.7 4.7-5.3 7.1-3.2 5.7.3.4h.7l12-2.6 6.4-1.1 7.6-1.3 3.5 1.6.4 1.6-1.4 3.4-8.2 2-9.6 2-14.3 3.3-.2.1.2.3 6.4.6 2.8.2h6.8l12.6 1 3.3 2 1.9 2.7-.3 2-5.1 2.6-6.8-1.6-16-3.8-5.4-1.3h-.8v.4l4.6 4.5 8.3 7.5L89 80.1l.5 2.4-1.3 2-1.4-.2-9.2-7-3.6-3-8-6.8h-.5v.7l1.8 2.7 9.8 14.7.5 4.5-.7 1.4-2.6 1-2.7-.6-5.8-8-6-9-4.7-8.2-.5.4-2.9 30.2-1.3 1.5-3 1.2-2.5-2-1.4-3 1.4-6.2 1.6-8 1.3-6.4 1.2-7.9.7-2.6v-.2H49L43 72l-9 12.3-7.2 7.6-1.7.7-3-1.5.3-2.8L24 86l10-12.8 6-7.9 4-4.6-.1-.5h-.3L17.2 77.4l-4.7.6-2-2 .2-3 1-1 8-5.5Z" />
      </svg>
    );
  }
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: meta.bg,
        color: meta.color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.55,
        fontWeight: 700,
      }}
    >
      ?
    </Box>
  );
}

// Render a plain text segment with brand + source-domain highlighting applied.
function renderHighlightedText(
  text: string,
  brandTerms: string[],
  sourceDomains: string[],
  keyPrefix: string,
): React.ReactNode[] {
  const allTerms = [...brandTerms, ...sourceDomains];
  if (!text || allTerms.length === 0) return [text];
  const regex = new RegExp(`(${allTerms.map(escapeRegex).join('|')})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) => {
    const lower = part.toLowerCase();
    const isBrand = brandTerms.some((t) => t.toLowerCase() === lower);
    const isSource = !isBrand && sourceDomains.some((d) => d === lower);
    if (isBrand) {
      return (
        <Box
          key={`${keyPrefix}-b-${i}`}
          component="span"
          sx={{
            bgcolor: '#00DDB340',
            color: '#00DDB3',
            px: 0.5,
            borderRadius: 0.5,
            fontWeight: 700,
          }}
        >
          {part}
        </Box>
      );
    }
    if (isSource) {
      return (
        <Box
          key={`${keyPrefix}-s-${i}`}
          component="span"
          sx={{
            bgcolor: 'rgba(124, 58, 237, 0.12)',
            color: '#7C3AED',
            px: 0.5,
            borderRadius: 0.5,
            fontWeight: 600,
            textDecoration: 'underline dotted',
          }}
        >
          {part}
        </Box>
      );
    }
    return <span key={`${keyPrefix}-t-${i}`}>{part}</span>;
  });
}

// Minimal inline markdown → React. Supports **bold**, *italic*, `code`, [text](url).
// Brand / source highlighting is applied on plain-text segments.
function renderInlineMarkdown(
  text: string,
  brandTerms: string[],
  sourceDomains: string[],
  keyPrefix: string,
): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  // Regex with grouped alternation: links, bold **/__, italic */_, inline code.
  const re =
    /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|__([^_]+)__|(?<![*\w])\*([^*\n]+)\*(?!\*)|(?<![_\w])_([^_\n]+)_(?!_)|`([^`]+)`/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let idx = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) {
      out.push(
        ...renderHighlightedText(
          text.slice(last, m.index),
          brandTerms,
          sourceDomains,
          `${keyPrefix}-p${idx}`,
        ),
      );
    }
    if (m[1] && m[2]) {
      // [label](url)
      out.push(
        <a
          key={`${keyPrefix}-a${idx}`}
          href={m[2]}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#8B9DC3', textDecoration: 'underline' }}
        >
          {renderHighlightedText(m[1], brandTerms, sourceDomains, `${keyPrefix}-a${idx}i`)}
        </a>,
      );
    } else if (m[3] || m[4]) {
      const inner = m[3] || m[4] || '';
      out.push(
        <strong key={`${keyPrefix}-b${idx}`}>
          {renderHighlightedText(inner, brandTerms, sourceDomains, `${keyPrefix}-b${idx}i`)}
        </strong>,
      );
    } else if (m[5] || m[6]) {
      const inner = m[5] || m[6] || '';
      out.push(
        <em key={`${keyPrefix}-i${idx}`}>
          {renderHighlightedText(inner, brandTerms, sourceDomains, `${keyPrefix}-i${idx}i`)}
        </em>,
      );
    } else if (m[7]) {
      out.push(
        <Box
          key={`${keyPrefix}-c${idx}`}
          component="code"
          sx={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: '0.85em',
            bgcolor: 'rgba(11,11,15,0.06)',
            px: 0.5,
            py: 0.2,
            borderRadius: 0.5,
          }}
        >
          {m[7]}
        </Box>,
      );
    }
    last = m.index + m[0].length;
    idx++;
  }
  if (last < text.length) {
    out.push(
      ...renderHighlightedText(
        text.slice(last),
        brandTerms,
        sourceDomains,
        `${keyPrefix}-p${idx}`,
      ),
    );
  }
  return out;
}

function HighlightedResponse({
  text,
  brandName,
  aliases,
  sources,
}: {
  text: string;
  brandName?: string;
  aliases?: string[];
  sources?: string[];
}) {
  const brandTerms = useMemo(() => {
    const t = [brandName, ...(aliases || [])].filter(Boolean) as string[];
    return t.sort((a, b) => b.length - a.length);
  }, [brandName, aliases]);

  const sourceDomains = useMemo(() => {
    const set = new Set<string>();
    (sources || []).forEach((url) => {
      const d = extractDomain(url);
      if (d && d.length >= 4) set.add(d);
    });
    return Array.from(set).sort((a, b) => b.length - a.length);
  }, [sources]);

  if (!text) {
    return (
      <Typography variant="body2" color="text.secondary">
        Pas de réponse enregistrée.
      </Typography>
    );
  }

  // Block-level markdown parser. Line-based: headings, horizontal rules,
  // unordered / ordered lists, blockquotes, blank-line paragraph breaks.
  //
  // Engine responses (esp. Perplexity) sometimes glue a heading onto the end
  // of a previous paragraph without a newline, e.g.
  //   "... réachats. #### 2. **Programmes de fidélité**"
  // The heading regex below requires ^, so we pre-split any inline "####"
  // run onto its own line. Same for inline horizontal rules ("--- foo").
  const normalized = text
    .replace(/\r\n/g, '\n')
    .replace(/([^\n])\s+(#{1,6}\s)/g, '$1\n\n$2');
  const lines = normalized.split('\n');
  const blocks: React.ReactNode[] = [];
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    const joined = paragraph.join(' ');
    blocks.push(
      <Typography
        key={`p-${blocks.length}`}
        variant="body2"
        sx={{ mb: 1, lineHeight: 1.65 }}
      >
        {renderInlineMarkdown(joined, brandTerms, sourceDomains, `p${blocks.length}`)}
      </Typography>,
    );
    paragraph = [];
  };

  const flushList = () => {
    if (!list) return;
    const { ordered, items } = list;
    blocks.push(
      <Box
        key={`l-${blocks.length}`}
        component={ordered ? 'ol' : 'ul'}
        sx={{ pl: 3, mb: 1, mt: 0 }}
      >
        {items.map((it, i) => (
          <Box component="li" key={i} sx={{ mb: 0.25 }}>
            <Typography variant="body2" component="span" sx={{ lineHeight: 1.65 }}>
              {renderInlineMarkdown(it, brandTerms, sourceDomains, `li${blocks.length}-${i}`)}
            </Typography>
          </Box>
        ))}
      </Box>,
    );
    list = null;
  };

  const flushAll = () => {
    flushParagraph();
    flushList();
  };

  const headingSx = (level: number) => ({
    mt: blocks.length === 0 ? 0 : 1.5,
    mb: 0.75,
    fontWeight: 700,
    fontSize:
      level === 1 ? '1.15rem' : level === 2 ? '1.05rem' : level === 3 ? '0.98rem' : '0.92rem',
    color: level <= 2 ? '#E8EAED' : '#B8C1D0',
  });

  for (const rawLine of lines) {
    const line = rawLine.replace(/\s+$/g, '');

    // Blank line → flush buffers
    if (!line.trim()) {
      flushAll();
      continue;
    }

    // Horizontal rule
    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      flushAll();
      blocks.push(
        <Box
          key={`hr-${blocks.length}`}
          sx={{ borderBottom: '1px solid rgba(11,11,15,0.10)', my: 1.25 }}
        />,
      );
      continue;
    }

    // Heading
    const headingMatch = /^(#{1,6})\s+(.+)$/.exec(line);
    if (headingMatch) {
      flushAll();
      const level = headingMatch[1].length;
      const content = headingMatch[2].replace(/\s+#+\s*$/, '');
      blocks.push(
        <Typography
          key={`h-${blocks.length}`}
          variant="body1"
          sx={headingSx(level)}
        >
          {renderInlineMarkdown(content, brandTerms, sourceDomains, `h${blocks.length}`)}
        </Typography>,
      );
      continue;
    }

    // Blockquote
    const bqMatch = /^\s{0,3}>\s?(.*)$/.exec(line);
    if (bqMatch) {
      flushAll();
      blocks.push(
        <Box
          key={`bq-${blocks.length}`}
          sx={{
            borderLeft: '3px solid rgba(11,11,15,0.16)',
            pl: 1.5,
            color: 'rgba(11,11,15,0.72)',
            fontStyle: 'italic',
            mb: 1,
          }}
        >
          <Typography variant="body2">
            {renderInlineMarkdown(bqMatch[1], brandTerms, sourceDomains, `bq${blocks.length}`)}
          </Typography>
        </Box>,
      );
      continue;
    }

    // Unordered list item
    const ulMatch = /^\s*[-*+]\s+(.+)$/.exec(line);
    if (ulMatch) {
      flushParagraph();
      if (!list || list.ordered) {
        flushList();
        list = { ordered: false, items: [] };
      }
      list.items.push(ulMatch[1]);
      continue;
    }

    // Ordered list item
    const olMatch = /^\s*\d+[.)]\s+(.+)$/.exec(line);
    if (olMatch) {
      flushParagraph();
      if (!list || !list.ordered) {
        flushList();
        list = { ordered: true, items: [] };
      }
      list.items.push(olMatch[1]);
      continue;
    }

    // Continuation of list item (indented)
    if (list && /^\s{2,}\S/.test(rawLine)) {
      list.items[list.items.length - 1] += ' ' + line.trim();
      continue;
    }

    // Fallback: paragraph text
    flushList();
    paragraph.push(line.trim());
  }

  flushAll();

  return <Box>{blocks}</Box>;
}

export default function BrandAxisPage({ axis }: { axis: BrandQueryAxis }) {
  const meta = AXIS_META[axis];
  const { activeBrand } = useBrand();
  const [audits, setAudits] = useState<Audit[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [selectedAudit, setSelectedAudit] = useState<Audit | null>(null);

  const loadAudits = useCallback(async () => {
    if (!activeBrand?.brandId) {
      setAudits([]);
      setLoading(false);
      return;
    }
    try {
      const data = await auditsApi.list(meta.auditType, activeBrand.brandId);
      setAudits(data);
    } catch {
      setError('Erreur lors du chargement des audits');
    } finally {
      setLoading(false);
    }
  }, [activeBrand?.brandId, meta.auditType]);

  useEffect(() => {
    setLoading(true);
    setSelectedAudit(null);
    loadAudits();
  }, [loadAudits]);

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    try {
      await auditsApi.create(meta.auditType, {}, activeBrand?.brandId);
      setDialogOpen(false);
      await loadAudits();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (selectedAudit) {
    return (
      <BrandAxisDetail
        axis={axis}
        audit={selectedAudit}
        onBack={() => setSelectedAudit(null)}
        onRefresh={async () => {
          const updated = await auditsApi.getById(selectedAudit.auditId);
          if (updated) setSelectedAudit(updated);
        }}
      />
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h4" fontWeight={700}>
          {meta.title}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          disabled={!activeBrand?.brandId}
        >
          Nouvel audit
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {meta.subtitle}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {activeBrand && (
        <QueryManager brand={activeBrand} axis={axis} />
      )}

      {audits.length === 0 ? (
        <Card>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              Aucun audit {meta.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Configurez votre liste de requêtes ci-dessus, puis lancez un audit pour mesurer
              comment les moteurs IA citent {activeBrand?.name || 'votre marque'}.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
              disabled={!activeBrand?.brandId}
            >
              Lancer un audit
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Card}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Agent</TableCell>
                <TableCell>Score</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {audits.map((audit) => {
                const agentStatus = audit.agentStatuses?.[meta.agentType] || 'pending';
                const score =
                  axis === 'narrative'
                    ? audit.brandNarrativeSummary?.score
                    : axis === 'authority'
                      ? audit.brandAuthoritySummary?.score
                      : audit.brandReferralSummary?.score;
                return (
                  <TableRow
                    key={audit.auditId}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setSelectedAudit(audit)}
                  >
                    <TableCell>
                      {new Date(audit.createdAt).toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_LABELS[audit.status] || audit.status}
                        color={STATUS_COLORS[audit.status] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={`${meta.title}: ${STATUS_LABELS[agentStatus] || agentStatus}`}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: AGENT_STATUS_COLORS[agentStatus] || '#555',
                          }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600}>
                        {score !== undefined ? `${score}/100` : '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nouvel audit — {meta.title}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body1">
              Lancer un audit {meta.title.toLowerCase()} pour{' '}
              <strong>{activeBrand?.name || '—'}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {meta.subtitle}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? <CircularProgress size={20} /> : 'Lancer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Detail view ──

function BrandAxisDetail({
  axis,
  audit: initialAudit,
  onBack,
  onRefresh,
}: {
  axis: BrandQueryAxis;
  audit: Audit;
  onBack: () => void;
  onRefresh: () => Promise<void>;
}) {
  const meta = AXIS_META[axis];
  const { activeBrand, refreshBrands } = useBrand();
  const [audit, setAudit] = useState(initialAudit);
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedZone, setSelectedZone] = useState('');
  const [enhancingKey, setEnhancingKey] = useState<string | null>(null);
  const [enhancePreview, setEnhancePreview] = useState<{
    query: string;
    currentStrategy: BrandStrategy;
    suggestedStrategy: BrandStrategy;
    currentGeoStrategy: GeoStrategy;
    suggestedGeoStrategy: GeoStrategy;
    explanation: string;
    hasBrandChange: boolean;
    hasGeoChange: boolean;
  } | null>(null);
  const [applying, setApplying] = useState(false);
  const [enhanceError, setEnhanceError] = useState('');
  const [enhanceSuccess, setEnhanceSuccess] = useState('');

  const summary = useMemo(() => {
    if (axis === 'narrative') return audit.brandNarrativeSummary;
    if (axis === 'authority') return audit.brandAuthoritySummary;
    return audit.brandReferralSummary;
  }, [axis, audit]);

  const loadData = useCallback(async () => {
    try {
      const [auditData, resultsData] = await Promise.all([
        auditsApi.getById(audit.auditId),
        auditsApi.getResults(audit.auditId, meta.agentType).catch(() => []),
      ]);
      if (auditData) setAudit(auditData);
      setResults(resultsData);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [audit.auditId, meta.agentType]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (axis === 'referral') {
      const referralSummary = summary as BrandReferralSummary | undefined;
      if (referralSummary?.zones && !selectedZone) {
        const zones = Object.keys(referralSummary.zones);
        if (zones.length > 0) setSelectedZone(zones[0]);
      }
    }
  }, [summary, selectedZone, axis]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    await onRefresh();
    setRefreshing(false);
  };

  const handleEnhance = useCallback(
    async (query: string, response: string) => {
      if (!activeBrand?.brandId) return;
      setEnhancingKey(query);
      setEnhanceError('');
      try {
        const res = await api.post<{
          success: boolean;
          explanation: string;
          currentStrategy: BrandStrategy;
          suggestedStrategy: BrandStrategy;
          currentGeoStrategy: GeoStrategy;
          suggestedGeoStrategy: GeoStrategy;
          hasBrandChange: boolean;
          hasGeoChange: boolean;
        }>(`/brands/${activeBrand.brandId}/strategy/suggest`, {
          query,
          response,
          axis,
          auditId: audit.auditId,
        });
        setEnhancePreview({
          query,
          currentStrategy: res.currentStrategy || {},
          suggestedStrategy: res.suggestedStrategy || {},
          currentGeoStrategy: res.currentGeoStrategy || {},
          suggestedGeoStrategy: res.suggestedGeoStrategy || {},
          explanation: res.explanation || '',
          hasBrandChange: !!res.hasBrandChange,
          hasGeoChange: !!res.hasGeoChange,
        });
      } catch (err: any) {
        setEnhanceError(err?.message || 'Erreur lors de la génération de suggestions');
      } finally {
        setEnhancingKey(null);
      }
    },
    [activeBrand?.brandId, audit.auditId, axis],
  );

  const handleApplyEnhancement = useCallback(
    async (applyBrand: boolean, applyGeo: boolean) => {
      if (!activeBrand?.brandId || !enhancePreview) return;
      setApplying(true);
      try {
        const requests: Promise<unknown>[] = [];
        if (applyBrand && enhancePreview.hasBrandChange) {
          requests.push(
            api.patch(
              `/brands/${activeBrand.brandId}/strategy`,
              enhancePreview.suggestedStrategy,
            ),
          );
        }
        if (applyGeo && enhancePreview.hasGeoChange) {
          requests.push(
            api.patch(
              `/brands/${activeBrand.brandId}/geo-strategy`,
              enhancePreview.suggestedGeoStrategy,
            ),
          );
        }
        await Promise.all(requests);
        setEnhanceSuccess(enhancePreview.query);
        setTimeout(() => setEnhanceSuccess(''), 3500);
        await refreshBrands();
        setEnhancePreview(null);
      } catch (err: any) {
        setEnhanceError(err?.message || 'Erreur lors de l’application');
      } finally {
        setApplying(false);
      }
    },
    [activeBrand?.brandId, enhancePreview, refreshBrands],
  );

  const agentStatus = audit.agentStatuses?.[meta.agentType] || 'pending';

  const filteredResults =
    axis === 'referral' && selectedZone
      ? results.filter((r) => r.zone === selectedZone)
      : results;

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>
        Retour
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          {meta.title} — {new Date(audit.createdAt).toLocaleDateString('fr-FR')}
        </Typography>
        <Chip
          label={STATUS_LABELS[audit.status] || audit.status}
          color={STATUS_COLORS[audit.status] || 'default'}
        />
        <Chip
          label={`Agent: ${STATUS_LABELS[agentStatus] || agentStatus}`}
          size="small"
          sx={{
            bgcolor: `${AGENT_STATUS_COLORS[agentStatus]}20`,
            color: AGENT_STATUS_COLORS[agentStatus],
            fontWeight: 600,
          }}
        />
        <Tooltip title="Rafraîchir">
          <IconButton onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {audit.status === 'running' && <LinearProgress sx={{ mb: 3, borderRadius: 1 }} />}

      {audit.status === 'pending' && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography color="text.secondary">Audit en attente de traitement…</Typography>
          </CardContent>
        </Card>
      )}

      {/* Axis-specific summary blocks */}
      {axis === 'narrative' && summary && (
        <NarrativeSummaryBlock summary={summary as BrandNarrativeSummary} color={meta.color} />
      )}
      {axis === 'authority' && summary && (
        <AuthoritySummaryBlock summary={summary as BrandAuthoritySummary} color={meta.color} />
      )}
      {axis === 'referral' && summary && (
        <ReferralSummaryBlock
          summary={summary as BrandReferralSummary}
          color={meta.color}
          selectedZone={selectedZone}
          onZoneChange={setSelectedZone}
        />
      )}

      {enhanceError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEnhanceError('')}>
          {enhanceError}
        </Alert>
      )}
      {enhanceSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setEnhanceSuccess('')}>
          Stratégie mise à jour pour : « {enhanceSuccess} »
        </Alert>
      )}

      {/* Results table */}
      <QueryResultsList
        results={filteredResults}
        loading={loading}
        brandName={activeBrand?.name}
        brandUrl={activeBrand?.url}
        aliases={activeBrand?.strategy?.aliases}
        showLevel={axis === 'referral'}
        showSources={axis === 'authority' || axis === 'referral' || axis === 'narrative'}
        showEnhance={axis === 'authority' || axis === 'referral'}
        onEnhance={handleEnhance}
        enhancingKey={enhancingKey}
      />

      <StrategyDiffDialog
        preview={enhancePreview}
        applying={applying}
        onClose={() => (applying ? undefined : setEnhancePreview(null))}
        onConfirm={handleApplyEnhancement}
      />

      {/* Recommendations */}
      {summary?.recommendations && summary.recommendations.length > 0 && (
        <Card sx={{ mb: 3, mt: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Recommandations
            </Typography>
            <List dense disablePadding>
              {summary.recommendations.map((rec, i) => (
                <RecItem
                  key={i}
                  rec={rec}
                  auditId={audit.auditId}
                  brandId={activeBrand?.brandId}
                />
              ))}
            </List>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

function NarrativeSummaryBlock({ summary, color }: { summary: BrandNarrativeSummary; color: string }) {
  return (
    <>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <SummaryCard title="Score Narrative" value={`${summary.score}`} suffix="/100" color={color} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <SummaryCard title="Requêtes" value={`${summary.query_count}`} suffix="" color="#7C3AED" />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <SummaryCard
            title="Alignement"
            value={ALIGNMENT_LABELS[summary.alignment] || summary.alignment}
            suffix=""
            color={ALIGNMENT_COLORS[summary.alignment] || '#888'}
          />
        </Grid>
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Perception IA
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {summary.key_perception}
          </Typography>
          {summary.pros?.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">Points forts</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {summary.pros.map((p, i) => (
                  <Chip key={i} label={p} size="small" sx={{ bgcolor: '#00DDB320', color: '#00DDB3' }} />
                ))}
              </Box>
            </Box>
          )}
          {summary.cons?.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">Points faibles</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {summary.cons.map((c, i) => (
                  <Chip key={i} label={c} size="small" sx={{ bgcolor: '#FF444420', color: '#FF4444' }} />
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function AuthoritySummaryBlock({ summary, color }: { summary: BrandAuthoritySummary; color: string }) {
  return (
    <>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <SummaryCard title="Score Autorité" value={`${summary.score}`} suffix="/100" color={color} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <SummaryCard
            title="Taux citation domaine"
            value={`${Math.round(summary.domain_citation_rate * 100)}`}
            suffix="%"
            color="#7C3AED"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <SummaryCard
            title="Mention dans réponse"
            value={`${Math.round((summary.response_text_mention_rate || 0) * 100)}`}
            suffix="%"
            color="#8B5CF6"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <SummaryCard title="Requêtes" value={`${summary.query_count}`} suffix="" color="#888" />
        </Grid>
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Autorité thématique
          </Typography>
          {summary.cited_topics?.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Topics où vous êtes cité comme source
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {summary.cited_topics.map((t, i) => (
                  <Chip key={i} label={t} size="small" color="success" />
                ))}
              </Box>
            </Box>
          )}
          {summary.missing_topics?.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Topics où vous n’êtes pas source
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {summary.missing_topics.map((t, i) => (
                  <Chip key={i} label={t} size="small" variant="outlined" />
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function ReferralSummaryBlock({
  summary,
  color,
  selectedZone,
  onZoneChange,
}: {
  summary: BrandReferralSummary;
  color: string;
  selectedZone: string;
  onZoneChange: (zone: string) => void;
}) {
  const zones = summary.zones ? Object.keys(summary.zones) : [];
  return (
    <>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <SummaryCard title="Score Referral" value={`${summary.score}`} suffix="/100" color={color} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <SummaryCard
            title="Taux citation"
            value={`${Math.round((summary.overall_citation_rate || 0) * 100)}`}
            suffix="%"
            color="#7C3AED"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <SummaryCard
            title="Taux recommandation"
            value={`${Math.round((summary.overall_recommendation_rate || 0) * 100)}`}
            suffix="%"
            color="#00DDB3"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <SummaryCard title="Requêtes" value={`${summary.query_count}`} suffix="" color="#888" />
        </Grid>
      </Grid>

      {zones.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="body2" color="text.secondary">Zone :</Typography>
          <Select
            value={selectedZone}
            onChange={(e) => onZoneChange(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
          >
            {zones.map((z) => (
              <MenuItem key={z} value={z}>{z}</MenuItem>
            ))}
          </Select>
        </Box>
      )}

      {selectedZone && summary.zones?.[selectedZone] && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Échelle de visibilité — {selectedZone}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Score zone : {summary.zones[selectedZone].score}/100
            </Typography>
            {(['L1', 'L2', 'L3', 'L4', 'L5'] as const).map((level) => {
              const data = summary.zones[selectedZone]?.levels?.[level];
              if (!data) return null;
              return (
                <Box key={level} sx={{ mb: 2 }}>
                  <ScoreBar
                    score={data.score}
                    label={`${LEVEL_LABELS[level]} (${data.cited}/${data.total} cité${data.cited > 1 ? 's' : ''})`}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    Poids : {Math.round(LEVEL_WEIGHTS[level] * 100)}%
                  </Typography>
                </Box>
              );
            })}
          </CardContent>
        </Card>
      )}
    </>
  );
}

function RecItem({ rec, auditId, brandId }: { rec: string; auditId: string; brandId?: string }) {
  const { tenant } = useAuth();
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const tasksEnabled = !!tenant?.settings?.featureFlags?.tasksEnabled;

  const handleAdd = async () => {
    if (added || adding) return;
    setAdding(true);
    try {
      await tasksApi.create({
        title: rec.slice(0, 120),
        description: rec,
        category: 'external',
        sourceType: 'audit_brand',
        auditId,
        brandId,
      });
      setAdded(true);
    } catch {
      /* ignore */
    } finally {
      setAdding(false);
    }
  };

  return (
    <ListItem disableGutters>
      <ListItemIcon sx={{ minWidth: 32 }}>
        <LightbulbIcon fontSize="small" sx={{ color: '#FF6B35' }} />
      </ListItemIcon>
      <ListItemText primary={rec} primaryTypographyProps={{ variant: 'body2' }} />
      {tasksEnabled && (
        <Tooltip title={added ? 'Ajouté aux tâches' : 'Ajouter aux tâches'}>
          <IconButton size="small" onClick={handleAdd} disabled={adding} sx={{ ml: 0.5 }}>
            {adding ? (
              <CircularProgress size={14} />
            ) : added ? (
              <CheckCircleOutlineIcon fontSize="small" sx={{ color: '#00DDB3' }} />
            ) : (
              <AddTaskIcon fontSize="small" sx={{ color: '#7C3AED' }} />
            )}
          </IconButton>
        </Tooltip>
      )}
    </ListItem>
  );
}

function QueryResultsList({
  results,
  loading,
  brandName,
  brandUrl,
  aliases,
  showLevel,
  showSources,
  showEnhance,
  onEnhance,
  enhancingKey,
}: {
  results: any[];
  loading: boolean;
  brandName?: string;
  brandUrl?: string;
  aliases?: string[];
  showLevel?: boolean;
  showSources?: boolean;
  showEnhance?: boolean;
  onEnhance?: (query: string, response: string) => void;
  enhancingKey?: string | null;
}) {
  const [expandedQuery, setExpandedQuery] = useState<string | null>(null);
  const [expandedEngine, setExpandedEngine] = useState<Record<string, EngineKey | null>>({});

  const grouped = useMemo(() => {
    const map = new Map<string, { query: string; level?: string; zone?: string; rows: any[] }>();
    for (const r of results) {
      const key = `${r.level || ''}|${r.zone || ''}|${r.query || ''}`;
      if (!map.has(key)) {
        map.set(key, { query: r.query, level: r.level, zone: r.zone, rows: [] });
      }
      map.get(key)!.rows.push(r);
    }
    return Array.from(map.entries()).map(([key, v]) => ({ key, ...v }));
  }, [results]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (results.length === 0) return null;

  const ENGINE_ORDER: EngineKey[] = ['perplexity', 'openai', 'gemini', 'claude'];

  return (
    <Card>
      <CardContent sx={{ pb: 0 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
          Requêtes ({grouped.length})
        </Typography>
      </CardContent>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={40} />
              <TableCell>Requête</TableCell>
              {showLevel && <TableCell>Niveau</TableCell>}
              <TableCell>Moteurs</TableCell>
              <TableCell>Cité</TableCell>
              {showSources && <TableCell>Sources</TableCell>}
              <TableCell>Recommandé</TableCell>
              <TableCell>Score</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {grouped.map((g) => {
              const isExpanded = expandedQuery === g.key;
              const byEngine = new Map<EngineKey, any>();
              g.rows.forEach((r) => byEngine.set(engineKey(r.engine), r));
              const orderedEngines = ENGINE_ORDER.filter((k) => byEngine.has(k));

              const citedCount = g.rows.filter((r) => r.cited).length;
              const recommendedCount = g.rows.filter((r) => r.recommended).length;
              const brandIsSourceAny = g.rows.some((r) => r.brandIsSource);
              const totalSources = g.rows.reduce((acc, r) => acc + (r.sources?.length || 0), 0);
              const avgScore = Math.round(
                g.rows.reduce((a, r) => a + (r.score || 0), 0) / g.rows.length,
              );
              const currentEngine = expandedEngine[g.key] ?? orderedEngines[0];

              return (
                <>
                  <TableRow key={g.key} hover sx={{ cursor: 'pointer' }}>
                    <TableCell onClick={() => setExpandedQuery(isExpanded ? null : g.key)}>
                      <IconButton size="small">
                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell
                      sx={{ maxWidth: 320 }}
                      onClick={() => setExpandedQuery(isExpanded ? null : g.key)}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        {g.query}
                      </Typography>
                      {g.zone && (
                        <Typography variant="caption" color="text.secondary">
                          {g.zone}
                        </Typography>
                      )}
                    </TableCell>
                    {showLevel && (
                      <TableCell onClick={() => setExpandedQuery(isExpanded ? null : g.key)}>
                        {g.level ? <Chip label={g.level} size="small" /> : '—'}
                      </TableCell>
                    )}
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {orderedEngines.map((k) => {
                          const r = byEngine.get(k)!;
                          const meta = ENGINE_META[k];
                          const isActive = isExpanded && currentEngine === k;
                          return (
                            <Tooltip key={k} title={`${meta.label} — score ${r.score}`}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedQuery(g.key);
                                  setExpandedEngine((prev) => ({ ...prev, [g.key]: k }));
                                }}
                                sx={{
                                  border: isActive ? `1.5px solid ${meta.color}` : '1.5px solid transparent',
                                  bgcolor: isActive ? meta.bg : 'transparent',
                                  width: 28,
                                  height: 28,
                                  '&:hover': { bgcolor: meta.bg },
                                }}
                              >
                                <EngineLogo engine={k} size={16} />
                              </IconButton>
                            </Tooltip>
                          );
                        })}
                      </Box>
                    </TableCell>
                    <TableCell onClick={() => setExpandedQuery(isExpanded ? null : g.key)}>
                      <Chip
                        label={`${citedCount}/${g.rows.length}`}
                        size="small"
                        color={citedCount > 0 ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </TableCell>
                    {showSources && (
                      <TableCell onClick={() => setExpandedQuery(isExpanded ? null : g.key)}>
                        {totalSources > 0 ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" fontWeight={600}>{totalSources}</Typography>
                            {brandIsSourceAny && (
                              <Chip
                                label="vous"
                                size="small"
                                sx={{
                                  bgcolor: '#00DDB320',
                                  color: '#00DDB3',
                                  fontWeight: 700,
                                  height: 20,
                                  fontSize: '0.7rem',
                                }}
                              />
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">0</Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell onClick={() => setExpandedQuery(isExpanded ? null : g.key)}>
                      <Chip
                        label={`${recommendedCount}/${g.rows.length}`}
                        size="small"
                        color={recommendedCount > 0 ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell onClick={() => setExpandedQuery(isExpanded ? null : g.key)}>
                      <Typography
                        fontWeight={700}
                        sx={{
                          color: avgScore >= 70 ? '#00DDB3' : avgScore >= 40 ? '#FF6B35' : '#FF4444',
                        }}
                      >
                        {avgScore}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow key={`${g.key}-expand`}>
                    <TableCell colSpan={99} sx={{ py: 0, border: isExpanded ? undefined : 'none' }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 1 }}>
                          {/* Engine tabs */}
                          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                            {orderedEngines.map((k) => {
                              const r = byEngine.get(k)!;
                              const meta = ENGINE_META[k];
                              const active = currentEngine === k;
                              return (
                                <Box
                                  key={k}
                                  onClick={() =>
                                    setExpandedEngine((prev) => ({ ...prev, [g.key]: k }))
                                  }
                                  sx={{
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    px: 1.5,
                                    py: 0.75,
                                    borderRadius: 1,
                                    border: `1.5px solid ${active ? meta.color : 'rgba(11,11,15,0.10)'}`,
                                    bgcolor: active ? meta.bg : 'transparent',
                                    transition: 'all 0.15s',
                                  }}
                                >
                                  <EngineLogo engine={k} size={18} />
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    sx={{ color: active ? meta.color : 'text.primary' }}
                                  >
                                    {meta.label}
                                  </Typography>
                                  <Chip
                                    label={`${r.score}`}
                                    size="small"
                                    sx={{
                                      height: 18,
                                      fontSize: '0.7rem',
                                      fontWeight: 700,
                                      bgcolor: 'rgba(11,11,15,0.04)',
                                    }}
                                  />
                                </Box>
                              );
                            })}
                          </Box>

                          {/* Selected engine panel */}
                          {currentEngine && byEngine.get(currentEngine) && (
                            <EngineResponsePanel
                              row={byEngine.get(currentEngine)}
                              engine={currentEngine}
                              brandName={brandName}
                              brandUrl={brandUrl}
                              aliases={aliases}
                              showSources={showSources}
                              showEnhance={showEnhance}
                              onEnhance={onEnhance}
                              enhancingKey={enhancingKey}
                              queryText={g.query}
                            />
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}

function EngineResponsePanel({
  row,
  engine,
  brandName,
  brandUrl,
  aliases,
  showSources,
  showEnhance,
  onEnhance,
  enhancingKey,
  queryText,
}: {
  row: any;
  engine: EngineKey;
  brandName?: string;
  brandUrl?: string;
  aliases?: string[];
  showSources?: boolean;
  showEnhance?: boolean;
  onEnhance?: (query: string, response: string) => void;
  enhancingKey?: string | null;
  queryText?: string;
}) {
  const meta = ENGINE_META[engine];
  const enhanceQuery = queryText || row.query;
  const enhanceLoading = !!enhanceQuery && enhancingKey === enhanceQuery;
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <EngineLogo engine={engine} size={20} />
        <Typography variant="subtitle2" fontWeight={700} sx={{ color: meta.color }}>
          {meta.label}
        </Typography>
        <Chip
          label={row.cited ? 'Cité' : 'Non cité'}
          size="small"
          color={row.cited ? 'success' : 'error'}
          variant="outlined"
          sx={{ height: 20 }}
        />
        <Chip
          label={row.recommended ? 'Recommandé' : 'Non recommandé'}
          size="small"
          color={row.recommended ? 'success' : 'default'}
          variant="outlined"
          sx={{ height: 20 }}
        />
        <Chip
          label={row.sentiment}
          size="small"
          sx={{
            height: 20,
            bgcolor:
              row.sentiment === 'positive'
                ? '#00DDB320'
                : row.sentiment === 'negative'
                  ? '#FF444420'
                  : '#8B9DC320',
            color:
              row.sentiment === 'positive'
                ? '#00DDB3'
                : row.sentiment === 'negative'
                  ? '#FF4444'
                  : '#8B9DC3',
            fontWeight: 600,
          }}
        />
        {row.narrativeAlignment && (
          <Chip
            label={`Alignement: ${ALIGNMENT_LABELS[row.narrativeAlignment] || row.narrativeAlignment}`}
            size="small"
            sx={{
              height: 20,
              bgcolor: `${ALIGNMENT_COLORS[row.narrativeAlignment] || '#888'}20`,
              color: ALIGNMENT_COLORS[row.narrativeAlignment] || '#888',
              fontWeight: 600,
            }}
          />
        )}
      </Box>

      <Box
        sx={{
          bgcolor: 'rgba(11,11,15,0.03)',
          borderLeft: `3px solid ${meta.color}`,
          borderRadius: 1,
          p: 2,
        }}
      >
        <HighlightedResponse
          text={row.response || ''}
          brandName={brandName}
          aliases={aliases}
          sources={row.sources || []}
        />
      </Box>

      {showSources && row.sources && row.sources.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Sources ({row.sources.length})
            {row.brandIsSource && (
              <Chip label="Votre site est source" size="small" color="success" sx={{ ml: 1 }} />
            )}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {row.sources.map((url: string, si: number) => {
              const isBrand = isBrandUrl(url, brandName, brandUrl);
              const domain = extractDomain(url);
              return (
                <Chip
                  key={si}
                  label={domain.slice(0, 50)}
                  size="small"
                  variant={isBrand ? 'filled' : 'outlined'}
                  sx={
                    isBrand
                      ? { bgcolor: 'rgba(16, 185, 129, 0.12)', color: '#10B981', fontWeight: 700 }
                      : { bgcolor: 'rgba(124, 58, 237, 0.10)', borderColor: 'rgba(124, 58, 237, 0.32)', color: '#7C3AED' }
                  }
                  onClick={() => window.open(url, '_blank')}
                />
              );
            })}
          </Box>
        </Box>
      )}

      {showEnhance && onEnhance && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={
              enhanceLoading ? (
                <CircularProgress size={14} sx={{ color: 'primary.main' }} />
              ) : (
                <TrendingUpIcon sx={{ color: 'primary.main' }} />
              )
            }
            disabled={enhanceLoading || !enhanceQuery}
            onClick={() => onEnhance(enhanceQuery, row.response || '')}
            sx={{
              borderColor: 'rgba(124, 58, 237, 0.5)',
              color: 'primary.main',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(124, 58, 237, 0.08)' },
            }}
          >
            {enhanceLoading ? 'Analyse…' : 'Apparaître davantage sur cette requête'}
          </Button>
        </Box>
      )}
    </Box>
  );
}

// ── Strategy diff dialog (Brand Knowledge + GEO Strategy) ──

const BRAND_ARRAY_FIELDS: (keyof BrandStrategy)[] = [
  'keywords',
  'scenarios',
  'differentiators',
  'aliases',
];
const BRAND_SCALAR_FIELDS: (keyof BrandStrategy)[] = [
  'industry',
  'description',
  'clientPersonas',
  'geographicFocus',
];
const BRAND_FIELD_LABELS: Partial<Record<keyof BrandStrategy, string>> = {
  keywords: 'Mots-clés',
  scenarios: 'Scénarios',
  differentiators: 'Différenciateurs',
  aliases: 'Alias',
  industry: 'Industrie',
  description: 'Description',
  clientPersonas: 'Personas client',
  geographicFocus: 'Zone géographique',
};

const GEO_ARRAY_FIELDS: (keyof GeoStrategy)[] = [
  'targetQueries',
  'targetAuthoritySources',
  'contentPillars',
  'competitiveFocus',
];
const GEO_SCALAR_FIELDS: (keyof GeoStrategy)[] = ['strategicAngle', 'queryStrategy'];
const GEO_FIELD_LABELS: Partial<Record<keyof GeoStrategy, string>> = {
  targetQueries: 'Requêtes cibles',
  targetAuthoritySources: 'Sources d’autorité cibles',
  contentPillars: 'Piliers de contenu',
  competitiveFocus: 'Concurrents prioritaires',
  strategicAngle: 'Angle stratégique',
  queryStrategy: 'Stratégie de requête',
};

function diffArrays(a: string[] = [], b: string[] = []) {
  const aSet = new Set(a);
  const bSet = new Set(b);
  return {
    added: b.filter((x) => !aSet.has(x)),
    removed: a.filter((x) => !bSet.has(x)),
  };
}

function ArrayFieldDiff({
  label,
  before,
  after,
}: {
  label: string;
  before: string[];
  after: string[];
}) {
  const { added, removed } = diffArrays(before, after);
  if (!added.length && !removed.length) return null;
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {added.map((v) => (
          <Chip
            key={`a-${v}`}
            label={`+ ${v}`}
            size="small"
            sx={{ bgcolor: '#00DDB320', color: '#00DDB3', fontWeight: 600 }}
          />
        ))}
        {removed.map((v) => (
          <Chip
            key={`r-${v}`}
            label={`− ${v}`}
            size="small"
            sx={{
              bgcolor: '#FF444420',
              color: '#FF4444',
              fontWeight: 600,
              textDecoration: 'line-through',
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

function ScalarFieldDiff({
  label,
  before,
  after,
}: {
  label: string;
  before: string;
  after: string;
}) {
  if (!after || before === after) return null;
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ bgcolor: 'rgba(255,68,68,0.08)', p: 1, borderRadius: 0.5, mb: 0.5 }}>
        <Typography
          variant="body2"
          sx={{ color: '#FF4444', textDecoration: 'line-through', whiteSpace: 'pre-wrap' }}
        >
          {before || '(vide)'}
        </Typography>
      </Box>
      <Box sx={{ bgcolor: 'rgba(0,221,179,0.08)', p: 1, borderRadius: 0.5 }}>
        <Typography variant="body2" sx={{ color: '#00DDB3', whiteSpace: 'pre-wrap' }}>
          {after}
        </Typography>
      </Box>
    </Box>
  );
}

function StrategyDiffDialog({
  preview,
  applying,
  onClose,
  onConfirm,
}: {
  preview: {
    query: string;
    currentStrategy: BrandStrategy;
    suggestedStrategy: BrandStrategy;
    currentGeoStrategy: GeoStrategy;
    suggestedGeoStrategy: GeoStrategy;
    explanation: string;
    hasBrandChange: boolean;
    hasGeoChange: boolean;
  } | null;
  applying: boolean;
  onClose: () => void;
  onConfirm: (applyBrand: boolean, applyGeo: boolean) => void;
}) {
  const [applyBrand, setApplyBrand] = useState(true);
  const [applyGeo, setApplyGeo] = useState(true);

  useEffect(() => {
    if (preview) {
      setApplyBrand(preview.hasBrandChange);
      setApplyGeo(preview.hasGeoChange);
    }
  }, [preview]);

  const hasAnyChange = !!preview && (preview.hasBrandChange || preview.hasGeoChange);
  const canApply =
    hasAnyChange &&
    ((preview!.hasBrandChange && applyBrand) || (preview!.hasGeoChange && applyGeo));

  return (
    <Dialog open={!!preview} onClose={applying ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUpIcon sx={{ color: '#7C3AED' }} />
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Apparaître davantage sur cette requête
            </Typography>
            <Typography variant="caption" color="text.secondary">
              « {preview?.query} »
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {preview?.explanation && hasAnyChange && (
          <Alert severity="info" icon={<LightbulbIcon />} sx={{ mb: 2 }}>
            {preview.explanation}
          </Alert>
        )}
        {!hasAnyChange && preview && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Aucun changement à appliquer : votre stratégie couvre déjà cette requête.
          </Alert>
        )}

        {preview?.hasBrandChange && (
          <Card variant="outlined" sx={{ mb: 2, borderColor: '#8B5CF680' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1.5,
                }}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#8B5CF6' }}>
                    Brand Knowledge
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Identité et positionnement de la marque
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant={applyBrand ? 'contained' : 'outlined'}
                  onClick={() => setApplyBrand((v) => !v)}
                  disabled={applying}
                  sx={
                    applyBrand
                      ? { bgcolor: '#8B5CF6', '&:hover': { bgcolor: '#7B4EE6' } }
                      : { borderColor: '#8B5CF680', color: '#8B5CF6' }
                  }
                >
                  {applyBrand ? 'Appliquer' : 'Ignorer'}
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {BRAND_ARRAY_FIELDS.map((f) => (
                  <ArrayFieldDiff
                    key={f}
                    label={BRAND_FIELD_LABELS[f] || f}
                    before={(preview.currentStrategy[f] as string[]) || []}
                    after={(preview.suggestedStrategy[f] as string[]) || []}
                  />
                ))}
                {BRAND_SCALAR_FIELDS.map((f) => (
                  <ScalarFieldDiff
                    key={f}
                    label={BRAND_FIELD_LABELS[f] || f}
                    before={(preview.currentStrategy[f] as string) || ''}
                    after={(preview.suggestedStrategy[f] as string) || ''}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        )}

        {preview?.hasGeoChange && (
          <Card variant="outlined" sx={{ borderColor: '#00DDB380' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1.5,
                }}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#00DDB3' }}>
                    GEO Strategy
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Plan d’action : requêtes, piliers, autorités à monitorer
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant={applyGeo ? 'contained' : 'outlined'}
                  onClick={() => setApplyGeo((v) => !v)}
                  disabled={applying}
                  sx={
                    applyGeo
                      ? { bgcolor: '#00DDB3', '&:hover': { bgcolor: '#00BFA3' } }
                      : { borderColor: '#00DDB380', color: '#00DDB3' }
                  }
                >
                  {applyGeo ? 'Appliquer' : 'Ignorer'}
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {GEO_ARRAY_FIELDS.map((f) => (
                  <ArrayFieldDiff
                    key={f}
                    label={GEO_FIELD_LABELS[f] || f}
                    before={(preview.currentGeoStrategy[f] as string[]) || []}
                    after={(preview.suggestedGeoStrategy[f] as string[]) || []}
                  />
                ))}
                {GEO_SCALAR_FIELDS.map((f) => (
                  <ScalarFieldDiff
                    key={f}
                    label={GEO_FIELD_LABELS[f] || f}
                    before={(preview.currentGeoStrategy[f] as string) || ''}
                    after={(preview.suggestedGeoStrategy[f] as string) || ''}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        )}

        {hasAnyChange && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Appliquer une modification re-passera la couche concernée en statut «&nbsp;draft / pending&nbsp;».
            Vous devrez la re-valider dans la page dédiée avant le prochain audit.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={applying}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={() => onConfirm(applyBrand, applyGeo)}
          disabled={applying || !canApply}
          startIcon={applying ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {applying ? 'Application…' : 'Appliquer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
